import { useEffect } from 'react';
import type { History, Location } from 'history';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import { effects as baseTemplateEffects } from '@rover/shared/js/ssr/BaseTemplate/BaseTemplate.duck';

import useCurrentUserShim from '../hooks/useCurrentUserShim';
import useDucks from '../hooks/useDucks';
import useFrontendConfigurationShim from '../hooks/useFrontendConfigurationShim';
import useFrontendEventsShim from '../hooks/useFrontendEventsShim';

import lifecycleEvents from './lifecycleEvents';

const effects = {
  fireDataLayerLoaded: baseTemplateEffects.fireDataLayerLoaded,
  fireLoadFullStoryJs: baseTemplateEffects.fireLoadFullStoryJs,
  fireLoadImpactJs: baseTemplateEffects.fireLoadImpactJs,
  fireLoadAmplitudeJs: baseTemplateEffects.fireLoadAmplitudeJs,
  fireLoadAmplitudeSessionReplayJs: baseTemplateEffects.fireLoadAmplitudeSessionReplayJs,
  fireRouteChange: baseTemplateEffects.fireRouteChange,
  fireSessionEvents: baseTemplateEffects.fireSessionEvents,
};

const useLifecycleEventsShim = (history: History): void => {
  const { user } = useCurrentUserShim();
  const { current: currentCountryConfig } = useCountryConfigurationShim();
  const { frontendEvents } = useFrontendEventsShim();
  const { isFlagActive } = useFrontendConfigurationShim();
  const { isReactQueryRoute, pageCategory } = usePageContext();

  const { data: selectedData } = useDucks({ effects });
  const events = isReactQueryRoute ? lifecycleEvents : selectedData;

  const {
    fireDataLayerLoaded,
    fireLoadFullStoryJs,
    fireLoadImpactJs,
    fireLoadAmplitudeJs,
    fireLoadAmplitudeSessionReplayJs,
    fireRouteChange,
    fireSessionEvents,
  } = events;

  useEffect(() => {
    frontendEvents && fireSessionEvents(frontendEvents);
  }, [frontendEvents]);

  useEffect(() => {
    user && fireLoadImpactJs({ currentUser: user });
  }, [user]);

  useEffect(() => {
    currentCountryConfig?.countryCode === 'US' && fireLoadAmplitudeJs();
  }, [currentCountryConfig]);

  useEffect(() => {
    user && fireDataLayerLoaded({ pageCategory, currentUser: user });
  }, [pageCategory, user]);

  useEffect(() => {
    const unlisten = history?.listen(({ pathname: p }: Location) => {
      fireRouteChange({ pathname: p });
    });
    return () => {
      unlisten?.();
    };
  }, []);

  useEffect(() => {
    fireLoadFullStoryJs(isFlagActive);
  }, [isFlagActive]);

  useEffect(() => {
    currentCountryConfig?.countryCode === 'US' && fireLoadAmplitudeSessionReplayJs(isFlagActive);
  }, [fireLoadAmplitudeSessionReplayJs, currentCountryConfig, isFlagActive]);
};

export default useLifecycleEventsShim;
